import { useEffect, useMemo, useState } from "react";
import { Button, Card, Checkbox, Cluster, FormField, Grid, Input, Stack, Textarea } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { api, ApiError } from "./api";
import { formatUtc } from "./format";
import { Flash } from "./Flash";
import { notifySaved } from "./savedToast";

interface SourceLink {
  title: string;
  url: string;
}

interface Jurisdiction {
  code: string;
  name: string;
  sources: SourceLink[];
  eea?: boolean;
}

interface ChecklistItem {
  id: string;
  label: string;
  done: boolean;
  done_at?: string | null;
}

interface ReviewCycle {
  id: string;
  period_start: string | null;
  period_end: string | null;
  status: string;
  checklist: ChecklistItem[];
  jurisdictions: Jurisdiction[];
  email_sent_at: string | null;
  completed_at: string | null;
  notes: string | null;
  overdue?: boolean;
}

interface PrivacyDashboard {
  interval_days: number;
  min_interval_days: number;
  max_interval_days: number;
  recipients: string[];
  open: ReviewCycle | null;
  history: ReviewCycle[];
  live_jurisdictions: Jurisdiction[];
  legal_urls: Record<string, string>;
  tooling_recommendation: {
    preferred: string;
    summary: string;
    doc: string;
  };
}

export function PrivacyPage() {
  const [data, setData] = useState<PrivacyDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleSaved, setScheduleSaved] = useState(false);
  const [intervalDays, setIntervalDays] = useState("90");
  const [recipientsText, setRecipientsText] = useState("");

  async function load() {
    setError(null);
    try {
      const payload = await api.get<PrivacyDashboard>("/operator/privacy-review");
      setData(payload);
      setNotes(payload.open?.notes ?? "");
      setIntervalDays(String(payload.interval_days));
      setRecipientsText(payload.recipients.join(", "));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to load privacy review");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const open = data?.open ?? null;
  const doneCount = useMemo(
    () => (open?.checklist ?? []).filter((item) => item.done).length,
    [open],
  );
  const totalCount = open?.checklist?.length ?? 0;

  async function toggleItem(item: ChecklistItem) {
    if (!open) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/operator/privacy-review/${open.id}/checklist`, {
        item_id: item.id,
        done: !item.done,
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to update checklist");
    } finally {
      setBusy(false);
    }
  }

  async function openCycle() {
    setBusy(true);
    setError(null);
    try {
      await api.post("/operator/privacy-review/open", {});
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to open review");
    } finally {
      setBusy(false);
    }
  }

  async function saveSchedule() {
    setBusy(true);
    setScheduleError(null);
    setScheduleSaved(false);
    try {
      const parsedInterval = Number(intervalDays);
      const recipients = recipientsText
        .split(",")
        .map((email) => email.trim())
        .filter(Boolean);
      await api.put("/operator/privacy-review/schedule", {
        interval_days: parsedInterval,
        recipients,
      });
      await load();
      setScheduleSaved(true);
      notifySaved("Privacy schedule saved.");
    } catch (err) {
      setScheduleError(err instanceof ApiError ? err.message : "Unable to save schedule");
    } finally {
      setBusy(false);
    }
  }

  async function sendReminder() {
    setBusy(true);
    setError(null);
    try {
      await api.post("/operator/privacy-review/send-reminder", {});
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to send reminder");
    } finally {
      setBusy(false);
    }
  }

  async function complete() {
    if (!open) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/operator/privacy-review/${open.id}/complete`, { notes: notes || null });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to complete review");
    } finally {
      setBusy(false);
    }
  }

  const jurisdictions = open?.jurisdictions?.length
    ? open.jurisdictions
    : (data?.live_jurisdictions ?? []);

  if (!data && !error) {
    return <p>Loading…</p>;
  }

  return (
    <Stack gap={5}>
      <Cluster justify="between" align="center">
        <div>
          <h1>Privacy review</h1>
          <p className="admin-muted">
            Continuous quarterly checklist ({data?.interval_days ?? 90} days). Worker emails{" "}
            {data?.recipients?.join(", ") || "support@saleslox.com"} when a cycle is due.
          </p>
        </div>
        <Cluster gap={2}>
          <Button variant="secondary" disabled={busy} onClick={() => sendReminder()}>
            Send reminder
          </Button>
          {!open ? (
            <Button disabled={busy} onClick={() => openCycle()}>
              Open review cycle
            </Button>
          ) : null}
        </Cluster>
      </Cluster>

      {error ? <Flash tone="error">{error}</Flash> : null}

      <Grid columns={2} gap={4}>
        <Card>
          <Stack gap={3}>
            <TipHeading
              label="Current cycle"
              tip="Open quarterly review: checklist items, window dates, and completion. Mark complete only when every item is done."
              as="h2"
            />
            {!open ? (
              <p className="admin-muted">
                No open review. Open a cycle or wait for the worker’s 90-day mail.
              </p>
            ) : (
              <>
                <p>
                  Window {formatUtc(open.period_start)} → {formatUtc(open.period_end)}
                  {open.overdue ? " · overdue" : ""}
                </p>
                <p className="admin-muted">
                  Checklist {doneCount}/{totalCount}
                  {open.email_sent_at ? ` · last email ${formatUtc(open.email_sent_at)}` : ""}
                </p>
                <Stack gap={2}>
                  {(open.checklist ?? []).map((item) => (
                    <Checkbox
                      key={item.id}
                      checked={item.done}
                      disabled={busy}
                      onChange={() => void toggleItem(item)}
                      label={item.label}
                    />
                  ))}
                </Stack>
                <FormField
                  label="Notes"
                  tooltip="Operator notes saved with this cycle when you mark it complete. Visible on the closed record."
                >
                  <Textarea
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    rows={3}
                    disabled={busy}
                  />
                </FormField>
                <Button disabled={busy || doneCount < totalCount} onClick={() => complete()}>
                  Mark review complete
                </Button>
              </>
            )}
          </Stack>
        </Card>

        <Card>
          <Stack gap={3}>
            <TipHeading
              label="Legal pages"
              tip="Live links to public legal documents on the marketing site. Open them during the quarterly pass."
              as="h2"
            />
            <Stack gap={2}>
              {Object.entries(data?.legal_urls ?? {}).map(([key, url]) => (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer">
                  {key.toUpperCase()}
                </a>
              ))}
            </Stack>
            <TipHeading
              label="Compliance tooling"
              tip="Platform recommendation for evidence and control tracking for this review cycle."
              as="h2"
            />
            <p>
              Preferred: <strong>{data?.tooling_recommendation?.preferred ?? "Vanta"}</strong>
            </p>
            <p className="admin-muted">{data?.tooling_recommendation?.summary}</p>
          </Stack>
        </Card>

        <Card>
          <Stack gap={3}>
            <TipHeading
              label="Schedule"
              tip="How often the worker opens a review cycle and re-mails a reminder, and who it notifies. Changes apply to the next cycle onward."
              as="h2"
            />
            <FormField
              label="Review interval (days)"
              tooltip={`Between ${data?.min_interval_days ?? 7} and ${data?.max_interval_days ?? 365} days. Currently ${data?.interval_days ?? 90}.`}
            >
              <Input
                type="number"
                min={data?.min_interval_days ?? 7}
                max={data?.max_interval_days ?? 365}
                value={intervalDays}
                onChange={(event) => setIntervalDays(event.target.value)}
                disabled={busy}
              />
            </FormField>
            <FormField
              label="Recipients"
              tooltip="Comma-separated email addresses the worker notifies when a cycle opens or is due for a reminder."
            >
              <Textarea
                value={recipientsText}
                onChange={(event) => setRecipientsText(event.target.value)}
                rows={2}
                disabled={busy}
              />
            </FormField>
            {scheduleError ? <Flash tone="error">{scheduleError}</Flash> : null}
            {scheduleSaved ? <p className="admin-muted">Saved.</p> : null}
            <Button disabled={busy} onClick={() => saveSchedule()}>
              Save schedule
            </Button>
          </Stack>
        </Card>
      </Grid>

      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Jurisdictions & regulatory sources"
            tip="Countries inferred from website visitors (180 days) and active org regions, with regulator source links to review each cycle."
            as="h2"
          />
          <p className="admin-muted">
            Derived from website visitor countries (180 days) plus active org regions. Review each
            source during the quarterly pass.
          </p>
          <div className="admin-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Jurisdiction</th>
                  <th>Sources</th>
                </tr>
              </thead>
              <tbody>
                {jurisdictions.map((row) => (
                  <tr key={row.code}>
                    <td>{row.code}</td>
                    <td>
                      {row.name}
                      {row.eea ? " · EEA" : ""}
                    </td>
                    <td>
                      <ul>
                        {(row.sources ?? []).map((source) => (
                          <li key={source.url}>
                            <a href={source.url} target="_blank" rel="noopener noreferrer">
                              {source.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <TipHeading
            label="History"
            tip="Closed and prior review cycles: period, status, reminder email time, and completion time."
            as="h2"
          />
          {!data?.history?.length ? (
            <p className="admin-muted">No prior cycles yet.</p>
          ) : (
            <div className="admin-table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Status</th>
                    <th>Email</th>
                    <th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((cycle) => (
                    <tr key={cycle.id}>
                      <td>
                        {formatUtc(cycle.period_start)} → {formatUtc(cycle.period_end)}
                      </td>
                      <td>
                        {cycle.status}
                        {cycle.overdue ? " (overdue)" : ""}
                      </td>
                      <td>{formatUtc(cycle.email_sent_at)}</td>
                      <td>{formatUtc(cycle.completed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
